import { PERMISSIONS, type SessionUser } from '@kdk/shared';
import { z } from 'zod';

const SESSION_KEY = 'kdk.session';

export type StoredSession = { token: string; user: SessionUser };

const StoredSessionSchema = z.object({
  token: z.string().min(1),
  user: z.object({
    id: z.string().min(1),
    name: z.string(),
    // 未知の権限を含む値は丸ごと不正とみなす(古い/改ざんされた保存値を信用しない)
    permissions: z.array(z.enum(PERMISSIONS)),
  }),
});

/** 保存された文字列を検証する。壊れていれば null(= 未ログイン)。 */
export const parseStoredSession = (raw: string | null): StoredSession | null => {
  if (raw === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  const parsed = StoredSessionSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

// sessionStorage が使えない環境(プライベートモード等)でも動くよう、メモリにも持つ。
let memorySession: StoredSession | null = null;

const removeStored = (): void => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // 使えない環境ではメモリだけで運用する
  }
};

export const readSession = (): StoredSession | null => {
  if (memorySession !== null) return memorySession;
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  const session = parseStoredSession(raw);
  if (session === null) {
    removeStored();
    return null;
  }
  memorySession = session;
  return session;
};

export const writeSession = (session: StoredSession): void => {
  memorySession = session;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // 使えない環境ではメモリだけで運用する
  }
};

export const clearSession = (): void => {
  memorySession = null;
  removeStored();
};

export const getToken = (): string | null => readSession()?.token ?? null;
